import type { HandleMeta } from '@/lib/canvas/types'
import {
  presentInpaint,
  type InpaintConnection,
  type InpaintPresentation,
} from '@genieartinc/schemas/pools/ai'
import { imageUrlFromFlowNode } from '@/lib/canvas/style-transfer-roles'

/**
 * Asset on an upstream node. A Mask node contributes its painted mask only —
 * never the photo loaded into the mask editor.
 */
export function inpaintAssetFromNode(node: { type?: string | null; data?: unknown } | null | undefined): {
  url?: string
  pending?: boolean
  unpainted?: boolean
} {
  if (!node?.data || typeof node.data !== 'object') return { pending: true }
  const data = node.data as Record<string, unknown>
  const state = (data.state && typeof data.state === 'object' ? data.state : data) as Record<string, unknown>
  if (node.type === 'MaskNode' || Object.prototype.hasOwnProperty.call(state, 'maskData')) {
    const painted = state.maskData
    if (typeof painted === 'string' && painted.startsWith('data:image/')) return { url: painted }
    return { unpainted: true }
  }
  const url = imageUrlFromFlowNode(node)
  return url ? { url } : { pending: true }
}

export function inpaintConnections(
  nodeId: string,
  handles: readonly HandleMeta[],
  nodes: readonly { id: string; type?: string | null; data?: unknown }[],
  edges: readonly { target?: string | null; targetHandle?: string | null; source?: string | null }[],
): InpaintConnection[] {
  return edges.flatMap((edge) => {
    if (edge.target !== nodeId || !edge.targetHandle) return []
    if (!handles.some((handle) => handle.id === edge.targetHandle && handle.type !== 'output')) return []
    const source = nodes.find((node) => node.id === edge.source)
    const asset = inpaintAssetFromNode(source)
    return [{ handleId: edge.targetHandle, ...asset }]
  })
}

export function inpaintPresentationForNode(input: {
  subPurpose?: string
  paramNames?: readonly string[]
  required?: readonly string[]
  handles?: readonly HandleMeta[]
  nodeId?: string
  nodes?: readonly { id: string; type?: string | null; data?: unknown }[]
  edges?: readonly { target?: string | null; targetHandle?: string | null; source?: string | null }[]
  selectedSourceUrl?: string
  savedFields?: Record<string, unknown>
}): InpaintPresentation | null {
  if (input.subPurpose !== 'inpaint' || !input.nodeId) return null
  const handles = input.handles ?? []
  return presentInpaint({
    subPurpose: input.subPurpose,
    paramNames: input.paramNames,
    required: input.required,
    selectedSourceUrl: input.selectedSourceUrl,
    savedFields: input.savedFields,
    handles: handles.map((handle) => ({
      id: handle.id,
      type: handle.type,
      dataType: handle.dataType,
      parameterName: handle.metadata?.parameterName,
      label: handle.label,
      ordered: typeof handle.position === 'object' && handle.position && 'ordered' in handle.position ? handle.position.ordered : undefined,
    })),
    connections: inpaintConnections(input.nodeId, handles, input.nodes ?? [], input.edges ?? []),
  })
}
